import React from 'react';
import {
    GiftCardTransaction,
    fakeItemsArray,
    useI18n,
    useSpringBigStore,
    useTransactionDetails,
    useTransactions,
    useTheme,
} from '@springbig/shared';
import { Section } from '~/shared/Theme';
import { Caption } from '~/shared/Typography';
import { useAppNavigate } from '~/pages';
import {
    Center,
    ChevronLeftIcon,
    ChevronRightIcon,
    HStack,
    Skeleton,
    VStack,
} from 'native-base';
import OurPressable from '~/shared/Pressable';
import { format } from 'date-fns';
import TransactionHeader from './TransactionHeader';
import { useNavigate, useLocation } from 'react-router-dom';
import { SpIcon } from '~/shared/Icon';
import { faReceipt } from '@fortawesome/free-solid-svg-icons';
import { Panel, Button } from '~/shared/Theme';

const Transactions = () => {

    const {
        primary,
    } = useTheme();

    const { params } = useAppNavigate();
    const navigate = useNavigate();
    const { state } = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const pageParam = searchParams.get('page');
    const { walletId } = useSpringBigStore();

    const { isLoading, giftCardTransactions, onNextPress, onPrevPress } =
        useTransactions(params, navigate, pageParam);

    return (
        <Section>
            <TransactionHeader />
            <OurPressable
                p={4}
                onPress={() => navigate({ pathname: `/w/${walletId}/giftCard` })}
            >
                <ChevronLeftIcon size={'lg'} color={primary} />
            </OurPressable>

            <VStack space={4}>
                {
                    giftCardTransactions &&
                    giftCardTransactions.transactions.length > 0 &&
                    <HStack px={4} justifyContent={'space-between'}>
                        <Caption text='giftCard.transactions.details.titleDate' />
                        <Caption text='giftCard.transactions.details.titleLoadAmount' />
                    </HStack>
                }

                { isLoading ?
                    fakeItemsArray.map((_, index) => (

                        <TransactionSkeleton key={index} />
                    ))
                    : giftCardTransactions &&
                        giftCardTransactions.transactions.length > 0 &&
                        giftCardTransactions.transactions.map((transaction) => (

                            <Panel row key={transaction?.id}>
                                <Transaction transaction={transaction} />
                            </Panel>
                        ))
                }
            </VStack>

            {
                giftCardTransactions &&
                giftCardTransactions.transactions.length > 0 &&
                    pageParam ? (
                        <HStack p={4} justifyContent={'space-between'}>
                            { parseInt(pageParam) > 1 && (
                                <Button
                                    text={'giftCard.transactions.prevPage'}
                                    variant={'text'}
                                    justifyContent={'center'}
                                    onPress={() => onPrevPress(state)}
                                />
                            )}
                            <VStack>
                                <Caption
                                    textAlign={'center'}
                                    text='giftCard.transactions.total'
                                    textValues={[giftCardTransactions?.count.toString()]}
                                />
                                <Caption
                                    textAlign={'center'}
                                    text='giftCard.transactions.pageOf'
                                    textValues={[
                                        pageParam,
                                        Math.ceil(giftCardTransactions?.count / 10).toString(),
                                    ]}
                                />
                            </VStack>
                            { giftCardTransactions?.count / 10 > parseInt(pageParam) && (
                                <Button
                                    text={'giftCard.transactions.nextPage'}
                                    variant={'text'}
                                    justifyContent={'center'}
                                    onPress={() => onNextPress(state)}
                                />
                            )}
                        </HStack>
                    ) : (
                        <VStack
                            flex={1}
                            space={4}
                            h={'100%'}
                            alignContent={'center'}
                            justifyContent={'center'}
                        >
                            <Caption
                                textAlign={'center'}
                                text='giftCard.transactions.empty_state'
                            />
                            <Center>
                                <SpIcon icon={faReceipt} size={100} color={primary} />
                            </Center>
                        </VStack>
                    )}
        </Section>
    );
};

const TransactionSkeleton = () => {

    const {
        primary,
    } = useTheme();

    return (
        <Panel
            row
            justifyContent={'space-between'}
            w={'full'}
            space={4}
        >
            <Skeleton.Text lines={2} w={20} />
            <VStack flex={1}>
                <Skeleton.Text
                    lines={1}
                    w={20}
                    justifyContent={'end'}
                />
                <Skeleton.Text
                    lines={1}
                    w={10}
                    justifyContent={'end'}
                    alignSelf={'end'}
                />
            </VStack>
            <ChevronRightIcon
                alignSelf={'center'}
                size={'lg'}
                color={primary}
            />
        </Panel>
    );
};

const Transaction = ({ transaction }: { transaction: GiftCardTransaction }) => {

    const {
        radius_sm,
        primary,
    } = useTheme();

    const { t } = useI18n();
    const { navigate, params } = useAppNavigate();
    const { state: { cardNumber } } = useLocation();
    const { walletId, merchant } = useSpringBigStore();
    const { isLoading, transactionRecords } = useTransactionDetails(
        params,
        transaction,
    );

    const dollarPerPoint = parseFloat(merchant?.dollars_per_point || '0.0');

    const onTransactionSelected = () => {

        if (walletId && params.gift_card_id && transaction.id) {
            navigate(
                `w/${walletId}/giftCard/${params.gift_card_id}/transaction/${transaction.id}`,
                {
                    state: { transaction, cardNumber },
                },
            );
        }
    };

    const returnHead = () => {

        if (transactionRecords?.type === t('giftCard.transactions.details.nmi')) {
            return <Caption text='giftCard.transactions.card_load' />;
        }

        return <Caption>{ transactionRecords?.type }</Caption>;
    };

    const returnSubHead = () => {

        if (transactionRecords?.type === t('giftCard.transactions.details.nmi')) {
            return (
                <Caption
                    textAlign={'right'}
                    text='giftCard.transactions.processing_fee'
                    textValues={[transaction.process_fee.toString()]}
                />
            );
        }
        else if (transactionRecords.type === t('giftCard.transactions.details.points_for_cash')) {
            return (
                <Caption
                    textAlign={'right'}
                    text='giftCard.transactions.transaction_points'
                    textValues={[(transaction.load_amount_cents / 100 * dollarPerPoint).toString()]}
                />
            );
        }
    };

    if (isLoading) {
        return <Skeleton rounded={radius_sm} w={'100%'} />;
    }

    return (
        <>
            <HStack justifyContent={'space-between'} flex={1} mr={3}>
                <VStack>
                    <Caption fontSize={18}>
                        { format(new Date(transaction.created_at), 'MM-dd-yyyy') }
                    </Caption>
                    { returnHead() }
                </VStack>
                <VStack>
                    <Caption fontSize={18} textAlign={'left'}>
                        ${ transaction.load_amount_cents / 100 }
                    </Caption>
                    { returnSubHead() }
                </VStack>
            </HStack>
            <OurPressable onPress={onTransactionSelected} justifyContent={'center'}>
                <ChevronRightIcon
                    alignSelf={'center'}
                    size={'lg'}
                    color={primary}
                />
            </OurPressable>
        </>
    );
};

export default Transactions;
